import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { supportedLanguages, type SupportedLanguage } from "~/lib/i18n";

interface LanguageSwitcherProps {
  variant?: "select" | "toggle";
  className?: string;
}

export function LanguageSwitcher({ variant = "select", className }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as SupportedLanguage;

  const handleLanguageChange = (language: SupportedLanguage) => {
    i18n.changeLanguage(language);
  };

  // Simple toggle between two languages
  if (variant === "toggle") {
    const otherLanguage = currentLanguage === "en-US" ? "es-MX" : "en-US";
    const currentLang = supportedLanguages.find((l) => l.code === currentLanguage);

    return (
      <Button
        variant="ghost"
        size="sm"
        className={className}
        onClick={() => handleLanguageChange(otherLanguage)}
        title={currentLang?.name}
      >
        <span className="text-sm font-medium">{currentLang?.iso2}</span>
        <span className="sr-only">{currentLang?.name}</span>
      </Button>
    );
  }

  // Dropdown select for multiple languages
  return (
    <Select value={currentLanguage} onValueChange={handleLanguageChange}>
      <SelectTrigger className={className}>
        <Globe className="h-4 w-4 mr-2" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {supportedLanguages.map((lang) => (
          <SelectItem key={lang.code} value={lang.code}>
            <span className="flex items-center gap-2">
              <span className="font-medium">{lang.iso2}</span>
              <span>{lang.name}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
